import { motion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContentContext";
import { Reveal, SectionTag } from "./Reveal";
import { serviceIcons } from "@/i18n/content";
import { PROJECT_IMAGES } from "@/i18n/assets";
import { toAbsolute } from "@/lib/api";

// map projects to relevant icons
const iconMap = [0, 2, 1, 3, 5, 6, 7, 4];

export default function Projects() {
  const { t, lang } = useLang();
  const { settings } = useSite();
  const items = t.projects.items.map((p, i) => ({
    title: settings[`project_${i}_title_${lang}`] || p,
    img: toAbsolute(settings[`project_${i}_image`]) || PROJECT_IMAGES[i],
  }));
  return (
    <section id="projects" className="relative section-pad" data-testid="projects-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl mb-14">
          <Reveal><SectionTag>{t.projects.tag}</SectionTag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5">{t.projects.title}</h2>
          </Reveal>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((p, i) => {
            const Icon = serviceIcons[iconMap[i]] || serviceIcons[0];
            return (
              <Reveal key={i} delay={(i % 4) * 0.06}>
                <Link to={`/projects/${i}`} className="block" data-testid={`project-card-link-${i}`}>
                  <motion.div whileHover={{ y: -6 }} className="group relative card-surface rounded-2xl h-52 overflow-hidden cursor-pointer" data-testid={`project-card-${i}`}>
                    <div className="absolute inset-0">
                      <img src={p.img} alt={p.title} loading="lazy" className="h-full w-full object-cover scale-105 group-hover:scale-110 transition-transform duration-700 opacity-45 group-hover:opacity-65" />
                      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,17,40,0.5), rgba(5,9,20,0.95))" }} />
                    </div>
                    {/* Play badge (hover) */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-14 w-14 rounded-full grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" style={{ background: "var(--primary)", boxShadow: "0 15px 40px -10px rgba(0,242,96,0.6)" }}>
                      <Play size={22} className="text-[#04120a] ml-0.5" />
                    </div>
                    <div className="relative z-10 h-full p-6 flex flex-col justify-between">
                      <div className="flex items-center justify-between">
                        <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: "rgba(0,242,96,0.15)", backdropFilter: "blur(4px)" }}>
                          <Icon size={22} className="text-primary" />
                        </div>
                        <ArrowUpRight size={20} className="text-slate-300 group-hover:text-primary transition-colors" />
                      </div>
                      <h3 className="font-bold text-lg">{p.title}</h3>
                    </div>
                  </motion.div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
